// Single source of truth for what search engines and link previews are told
// about Zimzo as a whole. The production domain used to be hardcoded in
// SEO.js, public/index.html and public/robots.txt separately, and all three
// were left pointing at the old thewafflestopsikkim.in domain - keep anything
// domain- or location-shaped here.
//
// public/index.html, robots.txt and sitemap.xml can't import this (they're
// static files), so they repeat SITE_URL and the Ravangla wording by hand -
// change them together.

export const SITE_URL = 'https://zimzo.shop';
export const SITE_NAME = 'Zimzo';

// Where Zimzo operates: every onboarded store is in or around Ravangla, Sikkim
// (PIN 737139). Rabongla and Rabong are the other spellings people search with.
export const SERVICE_AREA = {
  place: 'Ravangla',
  altNames: ['Rabongla', 'Rabong'],
  region: 'Sikkim',
  regionCode: 'IN-SK',
  postalCode: '737139',
  country: 'IN'
};

// Zimzo's own support channels - shown on Contact, in the footer, and on the
// Privacy Policy / Terms pages. public/index.html's Organization JSON-LD
// repeats the email, phone and address by hand - change them together.
export const CONTACT = {
  email: 'support@zimzo.shop',
  phoneDisplay: '+91 82070 90182',
  phoneE164: '+918207090182',
  instagramHandle: 'zimzo.shop',
  instagramUrl: 'https://www.instagram.com/zimzo.shop/',
  addressLines: ['C/o The Waffle Stop, Kewzing Road', 'Near Asian Plaza', 'Ravangla, Sikkim 737139']
};

// Who legally runs Zimzo, for the Privacy Policy (the DPDP Act's "Data
// Fiduciary") and the Terms (the contracting party), plus the grievance
// officer Indian e-commerce rules require to be named. The two bracketed
// placeholders MUST be replaced before the legal pages are deployed - check
// with: grep -rn "\[TO BE FILLED" src build
export const LEGAL = {
  operatorName: 'Reema Gautam',
  grievanceOfficer: 'Reema Gautam',
  lastUpdated: '3 October 2026'
};

// Delivery pincode -> the place name used in titles and structured data. A
// store's deliveryPincodes is the one location fact every store reliably has
// (their street addresses are mostly blank), so this is how a store page knows
// to say "delivery in Ravangla". Add an entry when a new pincode is served.
export const PINCODE_PLACES = {
  '737139': 'Ravangla'
};

export const DEFAULT_TITLE = 'Zimzo | Food, Café, Pizza & Grocery Delivery in Ravangla, Sikkim';
export const DEFAULT_DESCRIPTION =
  'Order food, coffee, pizza, snacks and groceries from local stores in Ravangla (Rabongla), Sikkim and get it delivered. Browse menus and order online on Zimzo.';

// No purpose-made 1200x630 share image exists yet; the 512px logo is the best
// real asset available for link previews.
export const DEFAULT_IMAGE = `${SITE_URL}/logo512.png`;

export const absoluteUrl = (path = '/') => `${SITE_URL}${path.startsWith('/') ? path : `/${path}`}`;

/** Place names a store delivers to, from its delivery pincodes, e.g. ['Ravangla']. */
export const storeServicePlaces = (store) =>
  [...new Set((store?.deliveryPincodes || []).map((pin) => PINCODE_PLACES[pin]).filter(Boolean))];

// ------------------------------------------------------------ structured data

export const breadcrumbJsonLd = (items) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    item: absoluteUrl(item.path)
  }))
});

export const storeListJsonLd = (stores) => ({
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  name: `Stores delivering in ${SERVICE_AREA.place}, ${SERVICE_AREA.region}`,
  itemListElement: stores.map((store, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    url: absoluteUrl(`/store/${store.id}`),
    name: store.name
  }))
});

const ALL_DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

/**
 * schema.org FoodEstablishment for a store page, including its full menu.
 *
 * Only states what the store data actually says. The address is included only
 * when the store has one filled in (most don't yet) - areaServed, derived from
 * its delivery pincodes, is what ties it to Ravangla until then. Hours are
 * stored as one open/close pair with no per-day schedule, so they apply daily.
 */
export const storeJsonLd = (store, { products = [], categories = [] } = {}) => {
  const url = absoluteUrl(`/store/${store.id}`);
  const places = storeServicePlaces(store);
  const address = store.address || {};
  const hasAddress = Boolean(address.street || address.city || address.state);

  const prices = products.map((product) => Number(product.price)).filter((price) => price > 0);
  const images = products.map((product) => product.imageUrl).filter(Boolean).slice(0, 3);

  const sections = categories
    .map((category) => ({
      '@type': 'MenuSection',
      name: category.name,
      hasMenuItem: products
        .filter((product) => product.categoryId === category.id)
        .map((product) => ({
          '@type': 'MenuItem',
          name: product.name,
          ...(product.description ? { description: product.description } : {}),
          ...(product.imageUrl ? { image: product.imageUrl } : {}),
          offers: { '@type': 'Offer', price: Number(product.price), priceCurrency: 'INR' }
        }))
    }))
    .filter((section) => section.hasMenuItem.length > 0);

  return {
    '@context': 'https://schema.org',
    '@type': 'FoodEstablishment',
    '@id': `${url}#store`,
    name: store.name,
    url,
    ...(images.length ? { image: images } : {}),
    ...(categories.length ? { servesCuisine: categories.map((category) => category.name) } : {}),
    ...(prices.length ? { priceRange: `₹${Math.min(...prices)} - ₹${Math.max(...prices)}` } : {}),
    ...(hasAddress
      ? {
          address: {
            '@type': 'PostalAddress',
            ...(address.street ? { streetAddress: address.street } : {}),
            ...(address.city ? { addressLocality: address.city } : {}),
            ...(address.state ? { addressRegion: address.state } : {}),
            addressCountry: SERVICE_AREA.country
          }
        }
      : {}),
    ...(places.length
      ? {
          areaServed: places.map((place) => ({
            '@type': 'Place',
            name: `${place}, ${SERVICE_AREA.region}`
          }))
        }
      : {}),
    ...(store.storeHours?.openTime && store.storeHours?.closeTime
      ? {
          openingHoursSpecification: {
            '@type': 'OpeningHoursSpecification',
            dayOfWeek: ALL_DAYS,
            opens: store.storeHours.openTime,
            closes: store.storeHours.closeTime
          }
        }
      : {}),
    ...(sections.length ? { hasMenu: { '@type': 'Menu', hasMenuSection: sections } } : {})
  };
};
